import { useAnalyticsClient } from '@acorns/web-utils'
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Exact, useVerifyPromoCodeLazyQuery } from 'generated/types'
import removeDuplicateQueryParams, { PROMO_CODE_KEY } from 'utils/sourceAttributes'
import { ValidationState } from './types'
import { useReferralCode } from './useReferralCode'
import { trackRegistrationPromoCodeErrorReceivedPayload } from 'generated/trackingEvents'
import { translateEventProps } from 'utils/analytics/eventSpec'
import { QueryLazyOptions } from '@apollo/react-hooks'

export interface PromoCodesProps {
  appliedPromoCode: string
  condensedTitle: string
  description: string
  hasPromoCodeQueryParam: boolean
  isDrawerOpen: boolean
  isErrorSubTextShown: boolean
  isPromoCodeVerificationLoading: boolean
  lastValidPromoCode: string
  onCloseDrawer: () => void
  promoCodeChannel: string
  setIsDrawerOpen: (isOpen: boolean) => void
  termsUrl: string | null
  validatePromoCode: (
    options?: QueryLazyOptions<
      Exact<{
        name: string
      }>
    >,
  ) => void
  validPromoCode?: string | null
  validationState: ValidationState
}

export const usePromoCodes = () => {
  const [validPromoCode, setValidPromoCode] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isErrorSubTextShown, setIsErrorSubTextShown] = useState(false)
  const [isQueryParamInput, setIsQueryParamInput] = useState(false)
  const [validationState, setValidationState] = useState<ValidationState>(
    ValidationState.EMPTY_INITIAL_STATE,
  )
  const [promoCodeChannel, setPromoCodeChannel] = useState('ORGANIC')
  const locationState = useLocation()
  const { hasReferralCode } = useReferralCode()
  const analytics = useAnalyticsClient()
  const { [PROMO_CODE_KEY]: promoCodeQueryParam } = removeDuplicateQueryParams(locationState.search)

  const [promoCodeCopies, setPromoCodeCopies] = useState({
    description: '',
    condensedTitle: '',
    appliedPromoCode: '',
    lastValidPromoCode: '',
    termsUrl: null,
  })

  const handleErrorResponse = () => {
    setPromoCodeCopies((prevState) => ({ ...prevState, appliedPromoCode: '' }))

    if (
      validationState === ValidationState.QUERY_PARAM_INVALID ||
      validationState === ValidationState.QUERY_PARAM_USER_INPUT_INVALID
    ) {
      setValidationState(ValidationState.QUERY_PARAM_USER_INPUT_INVALID)
    } else {
      setValidationState(
        isQueryParamInput
          ? ValidationState.QUERY_PARAM_INVALID
          : ValidationState.USER_INPUT_INVALID,
      )
    }

    setPromoCodeChannel('')

    if (isDrawerOpen) {
      setIsErrorSubTextShown(true)
    }

    setValidPromoCode(null)
  }

  const [validatePromoCode, { loading: isPromoCodeVerificationLoading }] =
    useVerifyPromoCodeLazyQuery({
      fetchPolicy: 'no-cache',
      onError: () => {
        handleErrorResponse()
        setIsQueryParamInput(false)
      },
      onCompleted: (data) => {
        if (data.verifyPromoCode.__typename === 'PromoCode') {
          setPromoCodeCopies({
            description: data.verifyPromoCode.description,
            condensedTitle: data.verifyPromoCode.condensedTitle,
            appliedPromoCode: data.verifyPromoCode.name,
            lastValidPromoCode: data.verifyPromoCode.name,
            termsUrl: data.verifyPromoCode.termsUrl,
          })
          setIsDrawerOpen(false)
          setValidationState(
            isQueryParamInput
              ? ValidationState.QUERY_PARAM_VALID
              : ValidationState.USER_INPUT_VALID,
          )
          setValidPromoCode(data.verifyPromoCode.name)
          setPromoCodeChannel(data.verifyPromoCode.channel)
        } else {
          handleErrorResponse()

          const [eventName, payLoad] = trackRegistrationPromoCodeErrorReceivedPayload({
            promotion: data.verifyPromoCode.name,
          })
          analytics.track(eventName, translateEventProps(payLoad))
        }

        setIsQueryParamInput(false)
      },
    })

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setIsErrorSubTextShown(false)
  }

  useEffect(() => {
    if (hasReferralCode) {
      return
    }

    if (promoCodeQueryParam) {
      setIsQueryParamInput(true)
      validatePromoCode({ variables: { name: promoCodeQueryParam.toString() } })
      return
    }
  }, [validatePromoCode, promoCodeQueryParam, hasReferralCode])

  return {
    appliedPromoCode: promoCodeCopies.appliedPromoCode,
    condensedTitle: promoCodeCopies.condensedTitle,
    description: promoCodeCopies.description,
    hasPromoCodeQueryParam: !!promoCodeQueryParam,
    isDrawerOpen,
    isErrorSubTextShown,
    isPromoCodeVerificationLoading,
    lastValidPromoCode: promoCodeCopies.lastValidPromoCode,
    onCloseDrawer: handleCloseDrawer,
    promoCodeChannel,
    setIsDrawerOpen,
    termsUrl: promoCodeCopies.termsUrl,
    validPromoCode,
    validatePromoCode,
    validationState,
  }
}
